import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Megaphone, Sparkles, CheckCircle2, Loader2 } from "lucide-react";

/**
 * `/promote` — book a paid campaign (top-listing boost / banner / hoarding / video ad).
 * Bookings created here are `booked` → pay → `pending_approval` → admin approves → `live`.
 *
 * Deep-link support: /promote?listing_id=list_xxx&sku=top_listing pre-selects the SKU
 * (default: top_listing when a listing_id arrives) and pre-fills the listing_id field.
 * Used by the "Promote" CTA on Dashboard listing cards.
 */

const SKU_ICONS = {
  top_listing: "🔝",
  banner:      "🖼️",
  hoarding:    "🎯",
  video_ad:    "🎬",
};

export default function Promote() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [mine, setMine] = useState([]);
  const [totals, setTotals] = useState({ impressions: 0, clicks: 0, ctr: 0 });
  const [selectedSku, setSelectedSku] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/ads/catalog").then(r => setCatalog(r.data));
    if (user) api.get("/ads/my-campaigns").then(r => {
      setMine(r.data.items || []);
      setTotals(r.data.totals || { impressions: 0, clicks: 0, ctr: 0 });
    });
  }, [user]);

  // Deep-link pre-fill — runs after the catalog loads so we can validate the sku.
  useEffect(() => {
    if (!catalog) return;
    const qListing = search.get("listing_id");
    const qSku = search.get("sku");
    if (qListing) setForm(f => ({ ...f, listing_id: qListing }));
    if (qSku && catalog[qSku]) setSelectedSku(qSku);
    else if (qListing && catalog.top_listing) setSelectedSku("top_listing");
  }, [catalog, search]);

  const submit = async () => {
    if (!user) { navigate("/login"); return; }
    if (!selectedSku || !selectedTier) return;
    setSubmitting(true);
    try {
      const body = {
        sku: selectedSku, tier_id: selectedTier.tier_id,
        listing_id: form.listing_id || null,
        category: form.category || null,
        city: form.city || null,
        start_at: form.start_at || null,
        creative_image_url: form.image_url || null,
        creative_video_url: form.video_url || null,
        creative_headline: form.headline || null,
        creative_cta: form.cta || null,
      };
      const r = await api.post("/ads/campaign", body);
      toast.success("Campaign booked. Now pay to submit for approval.");
      const c = r.data;
      // Immediately try wallet payment; user can switch to Stripe if wallet fails.
      try {
        const pay = await api.post(`/ads/campaign/${c.campaign_id}/pay`, { payment_mode: "wallet" });
        toast.success("Wallet debited · sent for admin approval.");
        setMine(m => [{ ...c, status: pay.data.status, payment_mode: "wallet" }, ...m]);
      } catch (err) {
        if (err?.response?.data?.detail?.reason === "insufficient_wallet") {
          toast.error("Wallet balance too low. Try Stripe checkout below or top up first.");
          const stripeR = await api.post(`/ads/campaign/${c.campaign_id}/pay`, {
            payment_mode: "stripe",
            origin_url: window.location.origin,
          });
          if (stripeR.data?.checkout_url) {
            window.location.href = stripeR.data.checkout_url;
            return;
          }
        } else { throw err; }
      }
      setSelectedSku(null); setSelectedTier(null); setForm({});
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not book");
    } finally { setSubmitting(false); }
  };

  if (!catalog) return <div className="p-8 text-stone-600">Loading catalog…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="promote-page">
      <div className="text-xs uppercase tracking-widest font-bold text-[#8B5E34] flex items-center gap-1.5"><Megaphone size={12}/> Promote your business</div>
      <h1 className="heading text-3xl md:text-4xl font-extrabold text-stone-900 mt-1">Get in front of every citizen searching nearby</h1>
      <p className="text-stone-600 mt-2">Pick a slot below. Pay by wallet or Stripe. Admin approves within 2 hours and your ad goes live for the selected window.</p>

      {/* SKU grid */}
      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {Object.entries(catalog).map(([sku, meta]) => (
          <button
            key={sku}
            onClick={() => { setSelectedSku(sku); setSelectedTier(null); }}
            className={`rounded-3xl border-2 p-5 text-left transition-all ${selectedSku === sku ? "border-[#E05E36] bg-orange-50/50" : "border-stone-200 bg-white hover:border-[#E05E36]/60"}`}
            data-testid={`ads-sku-${sku}`}
          >
            <div className="text-3xl">{SKU_ICONS[sku]}</div>
            <div className="mt-2 heading text-lg font-extrabold text-stone-900">{meta.label}</div>
            <div className="text-sm text-stone-600 mt-1">{meta.description}</div>
            <div className="mt-3 text-xs font-bold text-stone-500">Starting at ₹{meta.tiers[0].price_inr}</div>
          </button>
        ))}
      </div>

      {/* Tier + creative form */}
      {selectedSku && (
        <div className="mt-8 rounded-3xl border border-stone-200 bg-white p-5 md:p-6">
          <div className="heading text-xl font-extrabold text-stone-900">Pick a duration</div>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            {catalog[selectedSku].tiers.map(t => (
              <button key={t.tier_id} onClick={() => setSelectedTier(t)}
                      className={`rounded-2xl border-2 p-4 text-left ${selectedTier?.tier_id === t.tier_id ? "border-[#E05E36] bg-orange-50/50" : "border-stone-200 hover:border-[#E05E36]/60"}`}
                      data-testid={`ads-tier-${t.tier_id}`}>
                <div className="font-bold text-stone-900">{t.days} {t.days === 1 ? "day" : "days"}</div>
                <div className="text-2xl font-extrabold text-[#E05E36] mt-1">₹{t.price_inr}</div>
                <div className="text-xs text-stone-500 mt-1">or {t.price_pts.toLocaleString()} reward pts</div>
              </button>
            ))}
          </div>

          {selectedTier && (
            <div className="mt-6 space-y-3">
              {catalog[selectedSku].requires_listing && (
                <input placeholder="Your listing ID (list_...) — from your Dashboard"
                       value={form.listing_id || ""}
                       onChange={e => setForm(f => ({ ...f, listing_id: e.target.value }))}
                       className="w-full border border-stone-300 rounded-xl px-3 py-2 font-mono" data-testid="ads-listing-id"/>
              )}
              {catalog[selectedSku].requires_image && (
                <input placeholder="Creative image URL (banner or hero image)" onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                       className="w-full border border-stone-300 rounded-xl px-3 py-2" data-testid="ads-image-url"/>
              )}
              {catalog[selectedSku].requires_video && (
                <input placeholder="60-second video URL" onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))}
                       className="w-full border border-stone-300 rounded-xl px-3 py-2" data-testid="ads-video-url"/>
              )}
              <div className="grid sm:grid-cols-2 gap-3">
                <input placeholder="Category (optional — banner ads)" onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="border border-stone-300 rounded-xl px-3 py-2"/>
                <input placeholder="City (optional — top-listing)" onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="border border-stone-300 rounded-xl px-3 py-2"/>
              </div>
              <input placeholder="Headline (optional)" onChange={e => setForm(f => ({ ...f, headline: e.target.value }))} className="w-full border border-stone-300 rounded-xl px-3 py-2"/>
              <input placeholder="CTA text (optional — e.g. 'Book Now')" onChange={e => setForm(f => ({ ...f, cta: e.target.value }))} className="w-full border border-stone-300 rounded-xl px-3 py-2"/>
              <input placeholder="Requested start (ISO, optional — defaults to now)" onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))} className="w-full border border-stone-300 rounded-xl px-3 py-2"/>

              <button
                onClick={submit}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E05E36] hover:bg-[#c94e2a] text-white font-bold py-3 disabled:opacity-60"
                data-testid="ads-book-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>}
                {submitting ? "Booking…" : `Book & pay ₹${selectedTier.price_inr}`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* My campaigns */}
      {user && mine.length > 0 && (
        <div className="mt-10" data-testid="ads-analytics">
          <h2 className="heading text-xl md:text-2xl font-extrabold text-stone-900">Your campaigns</h2>
          {/* Roll-up analytics — one hero card summarising the full portfolio. */}
          <div className="mt-3 grid grid-cols-3 gap-3 bg-gradient-to-r from-stone-900 to-stone-800 text-white rounded-2xl p-4 md:p-5">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Total impressions</div>
              <div className="heading text-2xl md:text-3xl font-extrabold mt-0.5" data-testid="ads-total-impressions">{totals.impressions.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Total clicks</div>
              <div className="heading text-2xl md:text-3xl font-extrabold mt-0.5 text-[#E05E36]" data-testid="ads-total-clicks">{totals.clicks.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-white/60">Portfolio CTR</div>
              <div className="heading text-2xl md:text-3xl font-extrabold mt-0.5 text-[#DDA77B]" data-testid="ads-total-ctr">{totals.ctr}%</div>
            </div>
          </div>

          <div className="mt-3 space-y-2">
            {mine.map(c => (
              <div key={c.campaign_id} className="rounded-2xl border border-stone-200 bg-white p-4 flex items-center gap-4" data-testid={`ads-row-${c.campaign_id}`}>
                <div className="text-2xl">{SKU_ICONS[c.sku]}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-900">{c.sku.replace("_", " ")} · {c.tier_days}d</div>
                  <div className="text-xs text-stone-500">{c.campaign_id} · ₹{c.price_inr}</div>
                </div>
                <div className="hidden sm:flex items-center gap-4 text-center">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Impr.</div>
                    <div className="font-black text-stone-900" data-testid={`ads-impr-${c.campaign_id}`}>{(c.impressions || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Clicks</div>
                    <div className="font-black text-[#E05E36]" data-testid={`ads-clicks-${c.campaign_id}`}>{(c.clicks || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">CTR</div>
                    <div className="font-black text-stone-900" data-testid={`ads-ctr-${c.campaign_id}`}>{(c.ctr || 0)}%</div>
                  </div>
                </div>
                <StatusPill status={c.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    booked:            { cls: "bg-stone-100 text-stone-700", label: "Booked" },
    paid_pending:      { cls: "bg-amber-100 text-amber-800", label: "Awaiting payment" },
    pending_approval:  { cls: "bg-sky-100 text-sky-800",     label: "Pending approval" },
    live:              { cls: "bg-emerald-100 text-emerald-800", label: "Live" },
    rejected:          { cls: "bg-red-100 text-red-800",     label: "Rejected" },
    cancelled:         { cls: "bg-stone-100 text-stone-500", label: "Cancelled" },
    ended:             { cls: "bg-stone-100 text-stone-500", label: "Ended" },
  }[status] || { cls: "bg-stone-100 text-stone-700", label: status };
  return (
    <span className={`text-xs font-black uppercase tracking-widest px-2 py-1 rounded-full ${map.cls}`}>
      {status === "live" && <CheckCircle2 size={10} className="inline mr-1"/>}
      {map.label}
    </span>
  );
}
